/**
 * Build BSE /v2/add_ucc payload from admin/client UCC form state.
 */
export function buildUccAddPayload(form) {
  const cleanedBlob = String(form.aofBase64 || "")
    .replace(/^data:[^;]+;base64,/i, "")
    .replace(/\s+/g, "")
    .trim();
  let decodedSize = 0;
  try {
    decodedSize = atob(cleanedBlob).length;
  } catch {
    decodedSize = 0;
  }
  if (!cleanedBlob || decodedSize < 4) {
    throw new Error("Invalid or empty PDF. Please upload the application form again.");
  }

  const pan = String(form.pan || "").trim().toUpperCase();
  const phoneDigits = String(form.phone || "").replace(/\D/g, "").slice(-10);
  const memberId = String(form.member_id || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim() || "63164";
  const dpId = String(form.dp_id || "").trim();
  const clientId = String(form.client_id || "").trim();
  const hasDemat = /^[0-9]{8}$/.test(dpId) && /^[0-9]{8}$/.test(clientId);

  const data = {
    // SDK createPhysicalUcc uses member_code; keep member for compatibility
    member_code: { member_id: memberId },
    member: { member_id: memberId },
    investor: { client_code: String(form.client_code || "").trim() },
    holding_nature: form.holding_nature || "SI",
    tax_code: form.tax_code || "01",
    rdmp_idcw_pay_mode: form.rdmp_idcw_pay_mode || "02",
    // Physical when no demat; demat-only when DP IDs provided
    is_client_physical: !hasDemat,
    is_client_demat: hasDemat,
    is_nomination_opted: false,
    nomination_auth_mode: form.nomination_auth_mode || "O",
    comm_mode: form.comm_mode || "E",
    onboarding: form.onboarding || "Z",
    holder: [
      {
        holder_rank: "1",
        occ_code: form.occ_code || "02",
        auth_mode: form.auth_mode || "M",
        is_pan_exempt: false,
        kyc_type: form.kyc_type || "K",
        person: {
          first_name: String(form.first_name || "").trim(),
          last_name: String(form.last_name || "").trim(),
          dob: form.dob,
          gender: form.gender || "M",
        },
        contact: [
          {
            contact_number: phoneDigits,
            country_code: String(form.country_code || "91").replace(/\D/g, "") || "91",
            whose_contact_number: "SE",
            email_address: String(form.email || "").trim(),
            whose_email_address: "SE",
            contact_type: "PR",
          },
        ],
        identifier: [
          {
            identifier_type: "pan",
            identifier_number: pan,
          },
        ],
      },
    ],
    comm_addr: {
      address_line_1: String(form.addr1 || "").trim(),
      city: String(form.city || "").trim(),
      state: String(form.state || "").trim(),
      postalcode: String(form.postalcode || "").trim(),
      country: String(form.country || "INDIA").trim() || "INDIA",
    },
    bank_account: [
      {
        ifsc_code: String(form.ifsc_code || "").trim().toUpperCase(),
        bank_acc_num: String(form.bank_acc_num || "").trim(),
        bank_acc_type: form.bank_acc_type || "SB",
        account_owner: "SELF",
      },
    ],
    identifiers: [
      {
        identifier_type: "aof",
        file_name: "aof.pdf",
        file_blob: cleanedBlob,
        file_size: decodedSize,
        identifier_size: decodedSize,
      },
    ],
  };

  if (hasDemat) {
    data.depository = [
      {
        depository_code: "CDSL",
        dp_id: dpId,
        client_id: clientId,
        bank_account: String(form.bank_acc_num || "").trim(),
        account_owner: "SELF",
        is_default: true,
      },
    ];
  } else {
    // Physical UCC requires fatca (SDK createPhysicalUcc)
    const first = String(form.first_name || "").trim();
    const last = String(form.last_name || "").trim();
    data.fatca = [
      {
        holder_rank: "1",
        place_of_birth: String(form.place_of_birth || form.city || "").trim() || "India",
        country_of_birth: String(form.country_of_birth || form.fatca_nationality || "India").trim(),
        client_name: [first, last].filter(Boolean).join(" "),
        investor_type: "Individual",
        dob: form.dob,
        father_name: String(form.father_name || "").trim() || first,
        spouse_name: String(form.spouse || "").trim(),
        address_type: "1",
        occ_code: form.occ_code || "02",
        occ_type: "B",
        tax_status: "Individual",
        exemption_code: "A",
        Identifier: {
          identifier_type: "pan",
          identifier_number: pan,
        },
        corporate_service_sector: "1",
        wealth_source: "1",
        income_slab: "31",
        net_worth: Number(form.net_worth) || 0,
        date_of_net_worth: String(form.net_worth_as_on_date || "").trim() || undefined,
        politically_exposed:
          String(form.individual_pep_status || "").toLowerCase() === "yes" ? "Y" : "N",
        is_self_declared: true,
        data_source: "P",
        tax_residency: [
          {
            Country: "India",
            tax_id_no: pan,
            tax_id_type: "C",
          },
        ],
      },
    ];
  }

  return { data };
}
